import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, XCircle, Clock, Copy, Mail, Calendar, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending:  { label: "Aguardando", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
  accepted: { label: "Aceitou",    cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  declined: { label: "Recusou",    cls: "bg-red-50 text-red-800 border-red-200", Icon: XCircle },
};

export default function MemberInvites() {
  const [events, setEvents] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ event_slug: "", guest_name: "", guest_email: "", message: "" });
  const [lastInviteUrl, setLastInviteUrl] = useState("");

  const loadAll = async () => {
    try {
      const [evRes, invRes] = await Promise.all([
        axios.get(`${API}/members/events/active`, { withCredentials: true }),
        axios.get(`${API}/members/invites`, { withCredentials: true }),
      ]);
      const evs = evRes.data?.items || [];
      setEvents(evs);
      setInvites(invRes.data?.items || []);
      setForm((f) => ({ ...f, event_slug: f.event_slug || (evs[0]?.slug || "") }));
    } catch (e) {
      console.error("[Invites] load:", e?.message);
      toast.error("Não foi possível carregar a página.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const usedByEvent = useMemo(() => {
    const m = {};
    invites.forEach((i) => {
      if (i.status === "declined") return;
      m[i.event_slug] = (m[i.event_slug] || 0) + 1;
    });
    return m;
  }, [invites]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_slug) { toast.error("Selecione um evento."); return; }
    if (!form.guest_name.trim() || form.guest_name.trim().length < 2) {
      toast.error("Indique o nome do convidado."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.guest_email)) {
      toast.error("Email do convidado inválido."); return;
    }
    setSubmitting(true);
    setLastInviteUrl("");
    try {
      const r = await axios.post(`${API}/members/invites`, form, { withCredentials: true });
      toast.success("Convite enviado por email.");
      setLastInviteUrl(r.data?.invite_url || "");
      setForm({ event_slug: form.event_slug, guest_name: "", guest_email: "", message: "" });
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erro ao enviar convite.";
      toast.error(detail);
    } finally { setSubmitting(false); }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copiado.");
    } catch { toast.error("Não foi possível copiar."); }
  };

  if (loading) {
    return (
      <section className="container-page py-12" data-testid="member-invites-loading">
        <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
      </section>
    );
  }

  return (
    <section className="container-page py-10 lg:py-14" data-testid="member-invites-page">
      <button
        onClick={() => window.history.back()}
        className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] mb-3 inline-flex items-center gap-1.5"
      >
        ← Voltar
      </button>
      <div className="overline">Convidar para o próximo evento</div>
      <h1 className="display-heading text-3xl sm:text-4xl mt-3">Traga alguém consigo.</h1>
      <span className="accent-rule mt-5 block" />
      <p className="mt-6 max-w-2xl text-[hsl(var(--muted-foreground))] leading-relaxed">
        Como sócio do CIEES Portugal, pode convidar até <strong>10 pessoas por evento</strong>.
        Enviamos um email pessoal em seu nome e o convidado pode confirmar com um clique.
        Receberá uma notificação assim que houver resposta.
      </p>

      <div className="mt-10 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
        {/* FORM */}
        <form onSubmit={submit} className="bg-white border border-[hsl(var(--border))] p-6 lg:p-8" data-testid="invite-form">
          <div className="overline">Novo convite</div>
          <div className="mt-5 space-y-5">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Evento *</label>
              <select
                value={form.event_slug}
                onChange={(e) => setForm({ ...form, event_slug: e.target.value })}
                required
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="invite-event-select"
              >
                {events.length === 0 && <option value="">— sem eventos ativos —</option>}
                {events.map((ev) => {
                  const used = usedByEvent[ev.slug] || 0;
                  return (
                    <option key={ev.slug} value={ev.slug}>
                      {ev.title}{ev.date ? ` · ${ev.date}` : ""}{used > 0 ? ` (${used}/10 enviados)` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
            <Field label="Nome do convidado *" value={form.guest_name}
              onChange={(v) => setForm({ ...form, guest_name: v })} testid="invite-guest-name" />
            <Field label="Email do convidado *" type="email" value={form.guest_email}
              onChange={(v) => setForm({ ...form, guest_email: v })} testid="invite-guest-email" />
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">
                Mensagem pessoal (opcional)
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4} maxLength={600}
                placeholder="Conte porque acha que esta pessoa irá gostar do evento…"
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="invite-message"
              />
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{form.message.length}/600</div>
            </div>
          </div>
          <button
            type="submit" disabled={submitting || events.length === 0}
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[hsl(var(--primary))]/90 disabled:opacity-50 transition-colors"
            data-testid="invite-submit"
          >
            <Send className="h-3.5 w-3.5" /> {submitting ? "A enviar…" : "Enviar convite"}
          </button>
          {lastInviteUrl && (
            <div className="mt-5 p-3 bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Link enviado</div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] flex-1 break-all">{lastInviteUrl}</span>
                <button type="button" onClick={() => copy(lastInviteUrl)} className="text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]" data-testid="copy-last-invite">
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </form>

        {/* INVITES LIST */}
        <div>
          <div className="overline">Os meus convites</div>
          <h2 className="display-heading text-xl mt-2">{invites.length} {invites.length === 1 ? "convite enviado" : "convites enviados"}</h2>
          {invites.length === 0 ? (
            <p className="mt-6 text-sm text-[hsl(var(--muted-foreground))]">
              Ainda não enviou nenhum convite. Use o formulário ao lado para começar.
            </p>
          ) : (
            <ul className="mt-6 space-y-3" data-testid="invites-list">
              {invites.map((it) => {
                const b = STATUS_BADGE[it.status] || STATUS_BADGE.pending;
                const BIcon = b.Icon;
                return (
                  <li key={it.id} className="bg-white border border-[hsl(var(--border))] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">{it.guest_name}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 mt-0.5">
                          <Mail className="h-3 w-3" /> {it.guest_email}
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] border rounded-sm ${b.cls}`}>
                        <BIcon className="h-3 w-3" /> {b.label}
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {it.event_title}</span>
                      {it.event_date && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {it.event_date}</span>}
                      <span>· enviado {formatDate(it.created_at)}</span>
                      {it.responded_at && <span>· respondeu {formatDate(it.responded_at)}</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required
        className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
        data-testid={testid}
      />
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
  } catch { return iso; }
}
